import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { AppFrame } from "@/components/AppFrame";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Heading } from "@/components/primitives/Heading";
import { Hairline } from "@/components/primitives/Hairline";
import type { Topic } from "@offsite/shared";

/**
 * Beacon Topics-Stage (`/workshop/topics`).
 * Phase im Briefing nicht erwähnt — Beacon-Greenfield-Screen vor dem Voting.
 *
 * Themen werden nacheinander vorgestellt; die Vote-Phase folgt direkt.
 */
export function TopicsStage() {
  const { state } = useStore();
  const token = state.auth.token;
  const [topics, setTopics] = useState<Topic[]>([]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.topics.list(token);
        if (!cancelled) setTopics(res.topics);
      } catch {
        /* swallow */
      }
    };
    load();
    const id = setInterval(load, 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [token]);

  return (
    <AppFrame skin="presenter" hideHeader hideFooter>
      <section
        style={{
          flex: 1,
          padding: "var(--s-7)",
          display: "flex",
          flexDirection: "column",
          gap: "var(--s-7)",
        }}
      >
        <header
          className="rise-1"
          style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}
        >
          <Eyebrow accent>WORKSHOP · THEMEN</Eyebrow>
          <Heading level="display">Vier Themen.</Heading>
        </header>
        <Hairline skin="presenter" />
        <ol
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "var(--s-7)",
          }}
        >
          {topics.map((t, i) => (
            <li
              key={t.id}
              className={`rise-${Math.min(6, i + 2)}`}
              style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}
            >
              <span
                className="mono"
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 13,
                  letterSpacing: "var(--tracking-meta)",
                  textTransform: "uppercase",
                  color: "var(--accent-blue-soft)",
                }}
              >
                TOPIC {String.fromCharCode(65 + i)}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: "var(--t-h2)",
                  fontWeight: 700,
                  letterSpacing: "var(--tracking-h)",
                  color: "var(--svo-weiss, #fff)",
                }}
              >
                {t.title}
              </span>
              <Hairline skin="presenter" />
            </li>
          ))}
          {topics.length === 0 && (
            <li
              className="mono"
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 14,
                color: "var(--ink-400)",
                textTransform: "uppercase",
                letterSpacing: "var(--tracking-meta)",
              }}
            >
              KEINE THEMEN ANGELEGT
            </li>
          )}
        </ol>
      </section>
    </AppFrame>
  );
}
